export type NumericInput = number | string | null | undefined

export type FormatNumberOptions = Intl.NumberFormatOptions & {
  locale?: string
  fallback?: string
}

export type CurrencyConfig = {
  /** ISO 4217 code, e.g. RUB, AMD, BYN. */
  code: string
  /** Display symbol used by OSMIX geo settings, e.g. ₽, ֏, Br. */
  symbol: string
  /** Multiplier from base RUB price to the display currency. */
  rate?: number
  /** Whether to render the symbol before the amount. */
  symbolBefore?: boolean
}

export type FormatCurrencyOptions = Intl.NumberFormatOptions & {
  locale?: string
  currency?: string
  fallback?: string
}

export type FormatPriceOptions = FormatNumberOptions & {
  separator?: string
}

export type DimensionsInput =
  | { length?: NumericInput; width?: NumericInput; height?: NumericInput }
  | readonly [NumericInput, NumericInput, NumericInput]

export type FormatDimensionsOptions = FormatNumberOptions & {
  unit?: string
  separator?: string
}

export type FormatWeightOptions = FormatNumberOptions & {
  inputUnit?: "g" | "kg"
  displayUnit?: "auto" | "g" | "kg"
}

export type FormatDateishOptions = Intl.DateTimeFormatOptions & {
  locale?: string
  fallback?: string
}

export type FormatPhonePassthroughOptions = {
  fallback?: string
  trim?: boolean
}

export type SkuLike =
  | string
  | {
      sku?: string | null
      article?: string | null
      size?: string | null
      color?: string | null
    }

export type FormatSkuLabelOptions = {
  fallback?: string
  separator?: string
  includeSize?: boolean
  includeColor?: boolean
}

export type FormatSkuListLabelOptions = FormatSkuLabelOptions & {
  limit?: number
  listSeparator?: string
  emptyLabel?: string
  moreLabel?: (remaining: number) => string
}

export const DEFAULT_LOCALE = "ru-RU"
export const DEFAULT_FALLBACK = "—"
export const BASE_CURRENCY: CurrencyConfig = {
  code: "RUB",
  symbol: "₽",
  rate: 1,
  symbolBefore: false,
}

const toFiniteNumber = (value: NumericInput): number | undefined => {
  if (typeof value === "number") return Number.isFinite(value) ? value : undefined
  if (typeof value !== "string") return undefined

  const normalized = value.trim().replace(/\s+/g, "").replace(",", ".")
  if (!normalized) return undefined

  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : undefined
}

const trimTrailingZeros = (value: string): string => value.replace(/([,.]\d*?)0+$/, "$1").replace(/[,.]$/, "")

const isDimensionsTuple = (value: DimensionsInput): value is readonly [NumericInput, NumericInput, NumericInput] =>
  Array.isArray(value)

/** Locale-aware plain number formatter with a stable fallback for null/invalid input. */
export const formatNumber = (value: NumericInput, options: FormatNumberOptions = {}): string => {
  const { locale = DEFAULT_LOCALE, fallback = DEFAULT_FALLBACK, ...intlOptions } = options
  const n = toFiniteNumber(value)
  if (n == null) return fallback

  return new Intl.NumberFormat(locale, intlOptions).format(n)
}

/** Intl currency formatter for a raw amount already expressed in the target currency. */
export const formatCurrency = (value: NumericInput, options: FormatCurrencyOptions = {}): string => {
  const {
    locale = DEFAULT_LOCALE,
    fallback = DEFAULT_FALLBACK,
    currency = BASE_CURRENCY.code,
    maximumFractionDigits = 0,
    ...intlOptions
  } = options
  const n = toFiniteNumber(value)
  if (n == null) return fallback

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    maximumFractionDigits,
    ...intlOptions,
  }).format(n)
}

/** Format a base RUB price using an OSMIX geo currency config and display rate. */
export const formatPrice = (
  rubValue: NumericInput,
  currency: CurrencyConfig = BASE_CURRENCY,
  options: FormatPriceOptions = {},
): string => {
  const n = toFiniteNumber(rubValue)
  if (n == null) return options.fallback ?? DEFAULT_FALLBACK
  const displayValue = n * (currency.rate ?? 1)
  // Цена с копейками в рублях (продавец вправе указать 1 990,50) печатается
  // с копейками: раньше витрина показывала 1 991 ₽, а к оплате шло 1 990,50.
  // Целые суммы и пересчёт в другие валюты — как прежде, без дробной части.
  const hasKopecks =
    (currency.rate ?? 1) === 1 && Math.abs(Math.round(displayValue * 100) - Math.round(displayValue) * 100) > 0
  const {
    fallback = DEFAULT_FALLBACK,
    separator = " ",
    maximumFractionDigits = hasKopecks ? 2 : 0,
    ...numberOptions
  } = options
  const amount = formatNumber(displayValue, {
    fallback,
    maximumFractionDigits,
    ...(hasKopecks && options.maximumFractionDigits === undefined ? { minimumFractionDigits: 2 } : {}),
    ...numberOptions,
  })
  return currency.symbolBefore ? `${currency.symbol}${amount}` : `${amount}${separator}${currency.symbol}`
}

/** Format complete L × W × H dimensions. Returns fallback unless all three values are finite. */
export const formatDimensions = (dimensions: DimensionsInput | null | undefined, options: FormatDimensionsOptions = {}): string => {
  const { fallback = DEFAULT_FALLBACK, unit = "см", separator = " × ", maximumFractionDigits = 1, ...numberOptions } = options
  if (!dimensions) return fallback

  const values = isDimensionsTuple(dimensions)
    ? dimensions
    : ([dimensions.length, dimensions.width, dimensions.height] as const)
  const numbers = values.map(toFiniteNumber)
  if (numbers.some((n) => n == null)) return fallback

  const rendered = numbers.map((n) => trimTrailingZeros(formatNumber(n, { fallback, maximumFractionDigits, ...numberOptions })))
  return `${rendered.join(separator)} ${unit}`
}

/** Format a weight, accepting grams by default and rendering kg automatically at 1000g+. */
export const formatWeight = (value: NumericInput, options: FormatWeightOptions = {}): string => {
  const {
    fallback = DEFAULT_FALLBACK,
    inputUnit = "g",
    displayUnit = "auto",
    maximumFractionDigits,
    ...numberOptions
  } = options
  const n = toFiniteNumber(value)
  if (n == null) return fallback

  const grams = inputUnit === "kg" ? n * 1000 : n
  const unit = displayUnit === "auto" ? (Math.abs(grams) >= 1000 ? "kg" : "g") : displayUnit
  const displayValue = unit === "kg" ? grams / 1000 : grams
  const digits = maximumFractionDigits ?? (unit === "kg" ? 2 : 0)
  const rendered = trimTrailingZeros(formatNumber(displayValue, { fallback, maximumFractionDigits: digits, ...numberOptions }))

  return `${rendered} ${unit === "kg" ? "кг" : "г"}`
}

/** Format Date/string/number input; null, empty, and invalid dates return the fallback. */
export const formatDateish = (value: string | number | Date | null | undefined, options: FormatDateishOptions = {}): string => {
  const { locale = DEFAULT_LOCALE, fallback = DEFAULT_FALLBACK, dateStyle = "medium", ...intlOptions } = options
  if (value == null || value === "") return fallback

  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return fallback

  return new Intl.DateTimeFormat(locale, { dateStyle, ...intlOptions }).format(date)
}

/** Pass through already-formatted phone text, with optional trimming and empty fallback. */
export const formatPhonePassthrough = (
  value: string | null | undefined,
  options: FormatPhonePassthroughOptions = {},
): string => {
  const { fallback = DEFAULT_FALLBACK, trim = true } = options
  if (value == null) return fallback

  const rendered = trim ? value.trim() : value
  return rendered ? rendered : fallback
}

/** Build a readable SKU label from a SKU string or object with sku/article + variant hints. */
export const formatSkuLabel = (sku: SkuLike | null | undefined, options: FormatSkuLabelOptions = {}): string => {
  const {
    fallback = DEFAULT_FALLBACK,
    separator = " · ",
    includeSize = true,
    includeColor = false,
  } = options

  if (typeof sku === "string") return sku.trim() || fallback
  if (!sku) return fallback

  const base = (sku.sku ?? sku.article ?? "").trim()
  const details = [includeSize ? sku.size : undefined, includeColor ? sku.color : undefined]
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value))

  const parts = [base, ...details].filter(Boolean)
  return parts.length ? parts.join(separator) : fallback
}

/** Join SKU labels with an optional visible limit and compact "+N" overflow marker. */
export const formatSkuListLabel = (
  skus: readonly SkuLike[] | null | undefined,
  options: FormatSkuListLabelOptions = {},
): string => {
  const {
    emptyLabel = DEFAULT_FALLBACK,
    limit = 3,
    listSeparator = ", ",
    moreLabel = (remaining: number) => `+${remaining}`,
    ...labelOptions
  } = options
  if (!skus?.length) return emptyLabel

  const labels = skus
    .map((sku) => formatSkuLabel(sku, { fallback: "", ...labelOptions }))
    .filter((label) => label.length > 0)
  if (!labels.length) return emptyLabel

  const safeLimit = Math.max(1, Math.floor(limit))
  const visible = labels.slice(0, safeLimit)
  const remaining = labels.length - visible.length
  if (remaining > 0) visible.push(moreLabel(remaining))

  return visible.join(listSeparator)
}
